<template lang="pug">
  a.product-tile.u-no-link(
    :href="product.url"
    :class="tileClass"
    @mouseover="enter"
    @mouseleave="leave"
  )
    .product-tile__image-wrapper(v-if="image")
      .product-tile__best-seller.preheader.badge_bs(
        :style="bs_cssVars"
        v-if="isBestSeller && !horizontal"
      ) Best Seller
      .product-tile__best-seller.preheader.badge_np(
        :style="nr_cssVars"
        v-if="isNewProduct && !horizontal"
      ) New Product
      .product-tile__best-seller.preheader.badge_nr(
        :style="nr_cssVars"
        v-if="isNewProductRoast && !horizontal"
      ) New Roast
      .product-tile__best-seller.preheader.badge_so(
        :style="so_cssVars"
        v-if="isSingleOrigin && !horizontal"
      ) Single Origin
      .product-tile__best-seller.preheader.badge_le(
        :style="so_cssVars"
        v-if="isLimitedEdition && !horizontal"
      ) Limited Edition
      img.product-tile__image.product-tile__image--orig(
        v-lazy="image.src",
        :alt="image.alt"
      )
      img.product-tile__image.product-tile__image--hovered(
        v-lazy="altImage.src"
        :alt="altImage.alt"
      )
      .product-tile__badge(v-if="productBadge")
        .product-tile__badge__text {{ productBadge }}
    .product-tile__content(
      v-if="!horizontal"
    )
      product-bottom-line(
        :product-id="product.id"
        :display-no-reviews="false"
      )
      h3.product-tile__title {{ cleanTitle }}
      .product-tile__details
        p.product-tile__price(v-if="isOnSale")
          | {{ product.price | money }}
          span.product-tile__price__original {{ comparePrice | money }}
        p.product-tile__price(
          v-else-if="product.type === 'Gift Card'"
        ) {{ product.price | money }} - {{ maxProductPrice | money }}
        p.product-tile__price(
          v-else
        ) {{ product.price | money }}
        product-roast(
          v-if="showRoastLevel"
          :level="getRoastLevel"
        )
    .product-tile__content(
      v-else
    )
      product-bottom-line(
        :product-id="product.id"
        :display-no-reviews="false"
        v-if="$mq !== 'horizontal'"
      )
      .product-tile__title-wrapper
        h3.product-tile__title.product-tile__title-mini {{ cleanTitle }}
        p.product-tile__price(
          v-if="product.type === 'Gift Card'"
        ) {{ product.price | money }} - {{ maxProductPrice | money }}
        p.product-tile__price(
          v-else
        ) {{ product.price | money }}
      product-roast(
        v-if="showRoastLevel"
        :level="getRoastLevel"
      )
</template>

<script>
  import { getProductBadge, getSubscribeMessage, productIsBestSeller, productIsNew, productIsNewRoast, productIsSingleOrigin, productIsLimitedEdition } from 'scripts/helpers/tags.js';
  import { getSizedImageUrl } from 'scripts/helpers/images.js';
  import { getProductCleanTitle } from 'scripts/helpers/product.js';
  import images from 'scripts/config/images.js';

  import PrimaryButton from 'scripts/components/buttons/PrimaryButton.vue';
  import ProductBottomLine from 'scripts/components/product/ProductBottomLine.vue';
  import ProductRoast from 'scripts/components/product/ProductRoast.vue';

  export default {
    name: 'ProductTile',
    components: { PrimaryButton, ProductBottomLine, ProductRoast },
    data () {
      return {
        hovered: false,
        consumerLabSeal: images.consumerLabSeal,
      };
    },
    props: {
      product: Object,
      disableHover: {
        type: Boolean,
        default: false,
      },
      horizontal: {
        type: Boolean,
        default: false,
      },
      bs_color: {
        type: String,
        default: '#0A2B6F'
      },
      np_color: {
        type: String,
        default: '#0A2B6F'
      },
      nr_color: {
        type: String,
        default: '#0A2B6F'
      },
      so_color: {
        type: String,
        default: '#0A2B6F'
      },
      le_color: {
        type: String,
        default: '#0A2B6F'
      },
    },
    computed: {
      maxProductPrice() {
        return this.product.variants.reduce((price, variant) => {
          return Math.max(price, variant.price);
        }, this.product.price);
      },
      showRoastLevel() {
        return this.product.type === 'Coffee';
      },
      cleanTitle() {
        return getProductCleanTitle(this.product.title);
      },
      getRoastLevel() {
        return this.product.tags.reduce((level, tag) => {
          if (tag.includes('roast:')) {
            return tag.split(':')[1];
          }
          return level;
        }, 'medium');
      },
      tileClass () {
        return {
          'disable-hover': this.disableHover,
          'product-tile--horizontal': this.horizontal,
          'is-hovered' : this.hovered,
        };
      },
      image () {
        const image = this.product.images[0];
        const src = getSizedImageUrl(image.src, 'x390');
        return {
          src,
          alt: image.alt
        };
      },
      altImage () {
        if (this.product.images.length < 2) return this.image;
        const src = getSizedImageUrl(this.product.images[1].src, 'x390');
        return {
          src,
          alt: this.product.images[1].alt,
        };
      },
      isBestSeller () {
        return productIsBestSeller(this.product.tags);
      },
      bs_cssVars() {
      return {
        '--bs-border-color': this.bs_color,
        '--bs-color': this.bs_color
      }
    },
      isNewProduct () {
        return productIsNew(this.product.tags);
      },
      np_cssVars() {
        return {
          '--nr-border-color': this.np_color,
          '--nr-color': this.np_color
        }
    },
      isNewProductRoast () {
        return productIsNewRoast(this.product.tags);
      },
      nr_cssVars() {
        return {
          '--nr-border-color': this.nr_color,
          '--nr-color': this.nr_color
        }
    },
      isSingleOrigin () {
        return productIsSingleOrigin(this.product.tags);
      },
      so_cssVars() {
        return {
          '--so-border-color': this.so_color,
          '--so-color': this.so_color
        }
      },
      isLimitedEdition () {
        return productIsLimitedEdition(this.product.tags);
      },
      le_cssVars() {
        return {
          '--le-border-color': this.le_color,
          '--le-color': this.le_color
        }
      },
      productBadge () {
        return getProductBadge(this.product.tags);
      },
      comparePrice () {
        let price = this.product.variants[0].compare_at_price;
        return !isNaN(price) ? price : this.product.price;
      },
      isOnSale () {
        return this.comparePrice > this.product.price;
      },
      showSubscriptionMsg () {
        let isOneMonthOption = this.product.variants[0] && this.product.variants[0].option1 === '1-month'
        return this.product.subscription && isOneMonthOption;
      },
      subscribeMessage () {
        let message = getSubscribeMessage(this.product.tags);
        return message || 'Subscribe & Save';
      },
      showLabSeal() {
        return this.image && this.image.alt.toLowerCase().includes('consumer lab');
      },
    },
    methods: {
      enter () {
        if (!this.disableHover) this.hovered = true;
      },
      leave () {
        this.hovered = false;
      },
    },
  };
</script>

<style scoped lang="scss">
  .product-tile {
    position: relative;
    margin: 0 2px;
    padding: $grid-gutter $grid-gutter / 2;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    text-align: center;
    /* background-color: $color-white; */
    transition: 0.3s ease border-color;

    @include mobile-up {
      padding: $grid-gutter;
    }

    @include mobile-only {
      &.product-tile--large {
        padding: 18px 0;
      }
    }

    &.is-hovered {
      .product-tile__image--hovered {
        opacity: 1;
      }
      .product-tile__image--orig {
        opacity: 0;
      }
    }

    .slide & {
      height: 100%;
    }
  }

  .product-tile--horizontal {
    padding: 12px;
    flex-direction: row;
    text-align: left;
    justify-content: space-between;
    align-items: center;

    &:hover {
      border-color: $bg--main;
    }
  }

  .product-tile__image-wrapper {
    position: relative;
    flex-grow: 1;
    max-height: 185px;
    height: 100%;
    width: 100%;
    margin-bottom: $grid-gutter;

    @include mobile-only {
      .product-tile--large & {
        max-height: 245px;
      }
    }

    @include tablet-up {
      max-height: 388px;
    }

    .product-tile--horizontal & {
      margin-bottom: 0;
      margin-right: 18px;
      height: unset;
      min-height: unset;
      width: 90px;
      display: flex;
      flex-basis: 70px;
      flex-grow: 0;

      @include tablet-up {
        flex-basis: 90px;
      }
    }

    img {
      height: 100%;
      width: 100%;
      object-fit: contain;
    }
  }

  .product-tile__best-seller {
    position: absolute;
    left: 0px;
    top: 30px;
    border-radius: 100%;
    border: 1px solid $color-indigo;
    padding: 0 10px;
    width: 54px;
    height: 54px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: rem(13);
    line-height: 13px;
    font-family: $font-acumin-pro;
    font-weight: 500;
    text-transform: uppercase;
    color: $color-indigo;
    background-color: $color-white;
    z-index: 5;

    @include tablet-up {
      font-size: rem(16);
      line-height: 16px;
      left: 30px;
      width: 75px;
      height: 75px;
    }
  }

  .product-tile__badge {
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    right: 0;
    top: 0;
    padding: 3px 17px;
    background-color: $color--badge;

  }

  .product-tile__badge__text {
    color: $text--light;
    font-size: rem(12);
    font-weight: $font-weight--bold;
    line-height: 1.25;
    letter-spacing: 2.4px;
    text-transform: uppercase;
  }

  .product-tile__lab-seal {
    position: absolute;
    top: 10px;
    left: 10px;
    max-width: 26%;
  }

  .product-tile__image {
    display: block;
    margin: auto;
    max-height: 388px;
    transition: opacity .3s;

    .product-tile--horizontal & {
      margin: 0;
      max-width: 70px;
      align-self: center;
    }
  }

  .product-tile__image--hovered {
    position: absolute;
    top: 0;
    opacity: 0;
    /* background-color: $color-white; */
    transition: opacity .3s;
    z-index: 1;
  }

  .product-tile__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-grow: 1;

    @include tablet-up {
      align-items: flex-start;
    }

    .product-tile--horizontal & {
      align-items: flex-start;
    }
  }

  .product-tile__details {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;

    @include tablet-up {
      flex-direction: row;
    }
  }

  .product-tile__title-wrapper {
    margin-bottom: 6px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    width: 100%;

    .product-tile--horizontal & {
      margin-bottom: 0;
      align-items: center;
    }
  }

  .product-tile__title {
    margin: 0 0 6px;
    text-align: center;
    font-size: rem(15);
    line-height: 1.4;
    color: $color-indigo;

    @include tablet-up {
      text-align: left;
      font-size: rem(18);
    }

    .product-tile--horizontal & {
      margin-bottom: 0;
      text-align: left;
      font-size: rem(14);
      line-height: 1.28;
    }
  }

  .product-tile__price {
    margin: 0 0 5px;
    font-size: rem(14);
    line-height: 1.2;
    font-weight: 400;
    font-family: $font-sweet-sans;
    color: $color-indigo;

    @include tablet-up {
      font-size: rem(16);
    }

    &__original {
      display: inline-block;
      color: $color--badge;
      text-decoration: line-through;
      margin-left: 6px;
    }

    .product-tile--horizontal &,
    .product-tile--horizontal &__original {
      margin-bottom: 0;
      font-size: rem(14);
      line-height: 1.78;
    }
  }

  .product-tile__subscribe {
    font-size: rem(12);
    line-height: 1.9;
    text-transform: uppercase;
    letter-spacing: 2.4px;
    margin-bottom: 0;
    margin-top: auto;
    padding-top: 8px;
  }
  .badge_bs {
    border-color: var(--bs-border-color);
    color: var(--bs-color);
  }
  .badge_nr {
    border-color: var(--nr-border-color);
    color: var(--nr-color);
  }
  .badge_so {
    border-color: var(--so-border-color);
    color: var(--so-color);
  }
  .badge_le {
    border-color: var(--le-border-color);
    color: var(--le-color);
  }
</style>
